import { useAppDispatch } from '@shared/store';

import { modalInstanceRegistry } from './modalInstanceRegistry';
import type { ModalPropsMap, ModalType } from './modalPropsMap';
import {
  closeAllModals as closeAllModalsAction,
  closeModal as closeModalAction,
  openModal as openModalAction,
  replaceModal as replaceModalAction,
} from './modalStack';

type OpenModalArgs<Props> = {} extends Props
  ? [props?: Props, onCloseCallback?: () => void]
  : [props: Props, onCloseCallback?: () => void];

let modalIdCounter = 0;

const createModalId = () => {
  modalIdCounter += 1;

  return `modal-${modalIdCounter}`;
};

export const useModalActions = () => {
  const dispatch = useAppDispatch();

  const openModal = <T extends ModalType>(type: T, ...args: OpenModalArgs<ModalPropsMap[T]>) => {
    const [props, onCloseCallback] = args;
    const id = createModalId();

    modalInstanceRegistry.set(id, { props: props ?? {}, onCloseCallback });
    dispatch(openModalAction({ id, type, openedAt: Date.now() }));

    return id;
  };

  const closeModal = (id: string) => {
    const onCloseCallback = modalInstanceRegistry.getCloseCallback(id);

    modalInstanceRegistry.delete(id);
    dispatch(closeModalAction(id));
    onCloseCallback?.();
  };

  const replaceModal = <T extends ModalType>(id: string, type: T, ...args: OpenModalArgs<ModalPropsMap[T]>) => {
    const [props, onCloseCallback] = args;

    dispatch((innerDispatch, getState) => {
      if (!getState().modalStack.some((modal) => modal.id === id)) return;

      modalInstanceRegistry.replace(id, props ?? {}, onCloseCallback);
      innerDispatch(replaceModalAction({ id, type, openedAt: Date.now() }));
    });
  };

  const closeAllModals = () => {
    dispatch((innerDispatch, getState) => {
      const onCloseCallbacks = getState().modalStack.map(({ id }) => {
        const onCloseCallback = modalInstanceRegistry.getCloseCallback(id);

        modalInstanceRegistry.delete(id);

        return onCloseCallback;
      });

      innerDispatch(closeAllModalsAction());
      onCloseCallbacks.forEach((onCloseCallback) => onCloseCallback?.());
    });
  };

  return { openModal, closeModal, replaceModal, closeAllModals };
};
